import {
  Component,
  inject,
  OnInit,
  signal,
  ViewChild,
  ElementRef,
  effect,
  ChangeDetectionStrategy,
} from "@angular/core";
import {
  DocumentInterface,
  DocumentTypeEnum,
} from "src/app/core/interfaces/document-interface";
import { Button } from "src/app/shared/components/utils/button/button";
import { Icon } from "src/app/shared/components/utils/icon/icon";
import { FileDropZone } from "src/app/shared/components/utils/file-drop-zone/file-drop-zone";
import { DocumentService } from "../../services/document/document-service";
import { DatePipe } from "@angular/common";
import { Loader } from "src/app/shared/components/loader/loader";
import { Modal } from "src/app/shared/components/utils/modal/modal";
import { TranslocoModule, TranslocoService } from "@jsverse/transloco";
import { downloadFileFromResponse } from "src/app/shared/utils/download-file";

@Component({
  selector: "app-documents-page",
  imports: [
    Icon,
    Button,
    FileDropZone,
    DatePipe,
    Loader,
    Modal,
    TranslocoModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: "./documents-page.html",
})
export class DocumentsPage implements OnInit {
  @ViewChild("fileInput") fileInput?: ElementRef<HTMLInputElement>;
  private readonly documentService = inject(DocumentService);
  private readonly translocoService = inject(TranslocoService);

  loading = signal<boolean>(false);
  documents = signal<DocumentInterface[]>([]);
  isModalOpen = signal(false);
  documentSelected = signal<DocumentInterface | undefined>(undefined);

  fileTooHeavy = signal<boolean>(false);
  isErrorModalOpen = signal<boolean>(false);

  get currentLocale(): string {
    return this.translocoService.getActiveLang();
  }

  async addDocument(documents: File | File[]): Promise<void> {
    const docType = DocumentTypeEnum.Other;
    const filesArray = Array.isArray(documents) ? documents : [documents];

    for (const file of filesArray) {
      try {
        const response = await this.documentService.addDocument(docType, file);
        this.documents.update((docs) => {
          return [...docs, response];
        });
      } catch (error) {
        console.error("Erreur lors de l'ajout du document");
      }
    }
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const fileArray = Array.from(input.files);
      const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10Mo en bytes

      const validFiles = fileArray.filter((file) => file.size <= MAX_FILE_SIZE);
      const invalidFiles = fileArray.filter(
        (file) => file.size > MAX_FILE_SIZE
      );

      if (invalidFiles.length > 0) {
        this.fileTooHeavy.set(true);
        this.openErrorModal();
      } else {
        this.fileTooHeavy.set(false);
      }

      if (validFiles.length > 0) {
        for (const file of validFiles) {
          try {
            await this.addDocument(file);
          } catch (error) {
            console.error("Erreur lors de l'ajout du fichier:", file.name);
          }
        }
      }

      input.value = "";
    }
  }

  triggerFileInput(): void {
    this.fileInput?.nativeElement.click();
  }

  async deleteDocument(documentId: number): Promise<void> {
    await this.documentService.deleteDocument(documentId);
    this.documents.update((docs) => {
      return [...docs.filter((d) => d.documentId != documentId)];
    });
  }

  async downloadDocument(documentId: number): Promise<void> {
    const response = await this.documentService.downloadDocument(documentId);
    downloadFileFromResponse(response, "document.pdf");
  }

  async ngOnInit(): Promise<void> {
    this.loading.set(true);
    try {
      const documents = await this.documentService.getDocuments();
      this.documents.set(documents);
    } catch (error) {
      console.error("Erreur lors du chargement des documents:", error);
    } finally {
      this.loading.set(false);
    }
  }

  constructor() {
    effect(() => {
      document.body.style.overflow = this.isModalOpen() ? "hidden" : "";
      document.body.style.overflow = this.isErrorModalOpen() ? "hidden" : "";
    });
  }

  openModal(document?: DocumentInterface) {
    this.isModalOpen.set(true);
    this.documentSelected.set(document);
  }

  completeModal() {
    this.deleteDocument(this.documentSelected()!.documentId);
    this.closeModal();
  }

  closeModal() {
    this.isModalOpen.set(false);
  }

  setFileTooHeavy(show: boolean) {
    this.fileTooHeavy.set(show);
  }

  openErrorModal() {
    this.isErrorModalOpen.set(true);
  }

  closeErrorModal() {
    this.isErrorModalOpen.set(false);
  }
}
